import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

import { useUserApi } from '@/shared/services/userApi';
import useRedirectToApex from '@/shared/hooks/useRedirectToApex';

import IdentityCard from './IdentityCard';
import YourEcosystems from './YourEcosystems';

/**
 * AccountPage: the user's platform-level account surface, at `/account`.
 *
 * Renders two sections:
 *   1. IdentityCard: the editable name, photo, and bio stored on the platform
 *      User document. These values apply across every ecosystem the user
 *      belongs to.
 *   2. YourEcosystems: the membership manager, listing the ecosystems the user
 *      belongs to with join, leave, and open controls.
 *
 * The page renders only at the platform apex. `useRedirectToApex` sends any
 * visit that arrives with a tenant resolved to the apex `/account` URL.
 *
 * Per-ecosystem state (mentor profile, affiliations, network graph) is not
 * here; it lives on ProfilePage at `/{slug}/profile`.
 */
export default function AccountPage() {
    // The platform User record backing IdentityCard. Null until the fetch
    // below resolves.
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const userApi = useUserApi();
    // True while a redirect to the apex `/account` is in flight, which happens
    // when this page is reached with a tenant resolved.
    const isRedirectingToApex = useRedirectToApex('/account');

    // Load the platform profile on mount. Skipped entirely while a redirect is
    // in flight, since the browser is leaving this page.
    //
    // YourEcosystems fetches its own membership list, so this only needs the
    // profile.
    useEffect(() => {
        if (isRedirectingToApex) return;
        const fetchProfileData = async () => {
            try {
                const profileData = await userApi.fetchProfile();
                setProfile(profileData);
            } catch (err) {
                setError('Failed to load profile');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchProfileData();
    }, [userApi, isRedirectingToApex]);

    // Redirect in flight: show a spinner while the browser navigates away.
    if (isRedirectingToApex) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    // Profile fetch still running.
    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    // Fetch failed: show the message instead of the page.
    if (error) {
        return (
            <Container className="py-5 text-center">
                <p className="text-danger">{error}</p>
            </Container>
        );
    }

    // Re-reads the profile so the page reflects a save. Passed to IdentityCard
    // as `onUpdate`, which calls it after a successful submit. Does not touch
    // `loading`, so the page stays rendered while the values refresh.
    const refreshProfile = () => {
        userApi.fetchProfile().then(setProfile).catch(console.error);
    };

    return (
        <>
            <Helmet>
                <title>Account | RILE Connect</title>
            </Helmet>

            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col md={10} lg={8}>
                        <IdentityCard
                            profile={profile}
                            onUpdate={refreshProfile}
                        />
                        <YourEcosystems />
                    </Col>
                </Row>
            </Container>
        </>
    );
}
